/**
 * Google Analytics 4 追蹤工具
 *
 * 提供簡單的 API 來追蹤自訂事件和頁面瀏覽
 *
 * 使用範例：
 * ```javascript
 * import { trackEvent, trackPageView } from 'utils/analytics';
 *
 * // 追蹤搜尋教師
 * trackEvent('search', {
 *   search_term: 'Math teacher',
 *   filters_applied: 3
 * });
 *
 * // 追蹤預約開始
 * trackEvent('begin_checkout', {
 *   teacher_id: 123,
 *   event_type: 'trial_lesson'
 * });
 *
 * // 追蹤頁面瀏覽（用於 SPA 導航）
 * trackPageView('/teachers/123');
 * ```
 */

/**
 * 檢查 GA4 是否已載入
 * @returns {boolean} GA4 是否可用
 */
export const isGALoaded = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * 追蹤自訂事件
 *
 * @param {string} eventName - 事件名稱（例如：'search', 'begin_checkout', 'purchase'）
 * @param {Object} eventParams - 事件參數（可選）
 *
 * 建議的事件名稱：
 * - 'search' - 搜尋事件
 * - 'view_item' - 查看項目（例如教師檔案）
 * - 'select_content' - 選擇內容
 * - 'begin_checkout' - 開始結帳（例如開始預約）
 * - 'purchase' - 完成購買
 * - 'generate_lead' - 產生潛在客戶（例如提交合作請求）
 * - 'sign_up' - 註冊
 * - 'login' - 登入
 *
 * 詳細事件列表：https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (pageTracking.onEvent) {
    try {
      pageTracking.onEvent(eventName, eventParams);
    } catch (error) {
      // Page-level tracking must never stop the event itself, but a broken
      // tracker must not pass silently: it would report every visit as a bounce.
      console.error('[Analytics] page tracking failed:', error);
    }
  }

  if (!isGALoaded()) {
    console.warn('[Analytics] GA4 not loaded, event not tracked:', eventName);
    return;
  }

  const params = { ...pageTracking.params, ...eventParams };
  window.gtag('event', eventName, params);
  console.log('[Analytics] Event tracked:', eventName, params);
};

// Page-level tracking: one page (the team page) can attach shared params, such
// as flow_id, to every event and watch every event, without editing each call
// site. Params an event passes itself win over the shared ones.
//
// ⛔ Shared params go to Google with every event on the page, so only the keys
// below are accepted, and only as strings. Never add a name or email here.
const SHARED_PARAM_KEYS = ['flow_id', 'visitor_kind'];

let pageTracking = { params: {}, onEvent: null };

/**
 * @param {Object} options
 * @param {Object} options.params merged into every event's params
 * @param {Function} options.onEvent called with (eventName, eventParams) before sending, even when GA is not loaded
 */
export const setPageTracking = ({ params = {}, onEvent = null } = {}) => {
  const allowed = {};
  Object.entries(params).forEach(([key, value]) => {
    if (SHARED_PARAM_KEYS.includes(key) && typeof value === 'string') {
      allowed[key] = value;
    } else {
      console.warn('[Analytics] shared param dropped:', key);
    }
  });
  pageTracking = { params: allowed, onEvent };
};

export const clearPageTracking = () => {
  pageTracking = { params: {}, onEvent: null };
};

/**
 * 追蹤學生在公開頁切換課程
 *
 * 側邊欄與手機課程選單共用這個函式，靠 source 區分是哪一個介面。
 * 沒有這個事件就無法回答「改版後有沒有人真的在切換課程」。
 *
 * @param {Array} courses - 該團隊的課程列表
 * @param {number} courseId - 被選中的課程 id
 * @param {string} source - 觸發介面（'sidebar' 或 'mobile_picker'）
 */
export const trackCourseSwitch = (courses, courseId, source) => {
  const course = courses?.find((c) => c.id === courseId);

  trackEvent('course_switch', {
    course_name: course?.name || null,
    source
  });
};

/**
 * 追蹤頁面瀏覽（用於單頁應用導航）
 *
 * 注意：GA4 會自動追蹤傳統的頁面瀏覽，
 * 只有在使用 React Router 或其他 SPA 導航時才需要手動追蹤
 *
 * @param {string} pagePath - 頁面路徑（例如：'/teachers/123'）
 * @param {string} pageTitle - 頁面標題（可選）
 */
export const trackPageView = (pagePath, pageTitle = document.title) => {
  if (!isGALoaded()) {
    console.warn('[Analytics] GA4 not loaded, page view not tracked:', pagePath);
    return;
  }

  window.gtag('config', window.GA4_MEASUREMENT_ID || 'G-8HCG033T40', {
    page_path: pagePath,
    page_title: pageTitle
  });
  console.log('[Analytics] Page view tracked:', pagePath, pageTitle);
};

/**
 * 追蹤搜尋事件
 *
 * @param {string} searchTerm - 搜尋關鍵字
 * @param {Object} additionalParams - 額外參數（例如篩選器）
 *
 * 自動追蹤 referrer（來源頁面）：
 * - 如果 additionalParams 已包含 referrer，使用提供的值
 * - 否則使用 document.referrer
 * - 如果 document.referrer 為空（直接訪問），標記為 'direct'
 */
export const trackSearch = (searchTerm, additionalParams = {}) => {
  // 自動加入 referrer（如果未提供）
  const referrer = additionalParams.referrer ||
                   (typeof document !== 'undefined' && document.referrer) ||
                   'direct';

  trackEvent('search', {
    search_term: searchTerm,
    referrer: referrer,
    ...additionalParams
  });
};

/**
 * 追蹤查看教師檔案
 *
 * @param {number|string} teacherId - 教師 ID
 * @param {string} teacherName - 教師姓名（可選）
 */
export const trackViewTeacher = (teacherId, teacherName = null) => {
  const params = {
    item_id: teacherId.toString(),
    content_type: 'teacher_profile'
  };

  if (teacherName) {
    params.item_name = teacherName;
  }

  trackEvent('view_item', params);
};

/**
 * 追蹤開始預約
 *
 * @param {number|string} teacherId - 教師 ID
 * @param {string} eventType - 預約類型（例如：'trial_lesson', 'regular_lesson'）
 * @param {number} value - 預約價值（可選）
 */
export const trackBeginBooking = (teacherId, eventType = null, value = null) => {
  const params = {
    item_id: teacherId.toString(),
    content_type: 'booking'
  };

  if (eventType) {
    params.event_type = eventType;
  }

  if (value !== null) {
    params.value = value;
    params.currency = 'TWD';
  }

  trackEvent('begin_checkout', params);
};

/**
 * 追蹤完成預約（轉化事件）
 *
 * @param {number|string} bookingId - 預約 ID
 * @param {number} value - 預約價值
 * @param {Object} additionalParams - 額外參數
 */
export const trackCompleteBooking = (bookingId, value, additionalParams = {}) => {
  trackEvent('purchase', {
    transaction_id: bookingId.toString(),
    value: value,
    currency: 'TWD',
    ...additionalParams
  });
};

/**
 * 追蹤功能使用
 *
 * @param {string} featureName - 功能名稱（例如：'teacher_search_filters', 'collaboration_request'）
 * @param {Object} additionalParams - 額外參數
 */
export const trackFeatureUsage = (featureName, additionalParams = {}) => {
  trackEvent('feature_used', {
    feature_name: featureName,
    ...additionalParams
  });
};

/**
 * 追蹤 CTA 按鈕/連結點擊
 * 使用 beacon transport 確保頁面跳轉前事件能發送
 *
 * @param {string} ctaType - CTA 類型 ('teacher_signup', 'teacher_search')
 * @param {string} sourceLocation - 來源位置（例如：'homepage_hero', 'navbar_link', 'blog_post_teacher_cta'）
 * @param {string} destination - 目標 URL
 *
 * @example
 * // React 組件中使用
 * import { trackCTAClick } from 'utils/analytics';
 * <form onSubmit={() => trackCTAClick('teacher_signup', 'homepage_hero', '/auth')}>
 *
 * @example
 * // ERB view 中使用（透過 window）
 * <%= button_to "註冊", path, onclick: "window.trackCTAClick('teacher_signup', 'blog_cta', '/auth')" %>
 */
export const trackCTAClick = (ctaType, sourceLocation, destination = '') => {
  if (!isGALoaded()) {
    console.warn('[Analytics] GA4 not loaded, CTA click not tracked');
    return;
  }

  window.gtag('event', 'cta_click', {
    cta_type: ctaType,
    source_location: sourceLocation,
    destination: destination,
    event_label: `${ctaType}_${sourceLocation}`,
    transport: 'beacon'
  });

  console.log('[Analytics] CTA click tracked:', ctaType, sourceLocation);
};

/**
 * 簡易 A/B 測試
 *
 * 用 localStorage 保存分組，確保同一用戶每次看到同一 variant。
 * 回傳選中的 variant object。
 *
 * @param {string} experimentId - 實驗 ID（作為 localStorage key）
 * @param {Array<{id: string, label: string}>} variants - variant 清單
 * @returns {{id: string, label: string}} 選中的 variant
 */
export const getABVariant = (experimentId, variants) => {
  if (!variants || variants.length === 0) return null;

  const storageKey = `ab_${experimentId}`;
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const found = variants.find(v => v.id === saved);
      if (found) return found;
    }
  } catch (e) { /* localStorage unavailable */ }

  const selected = variants[Math.floor(Math.random() * variants.length)];
  try { localStorage.setItem(storageKey, selected.id); } catch (e) { /* ignore */ }
  return selected;
};

// 暴露到全域，供 ERB 和其他 JavaScript 使用
if (typeof window !== 'undefined') {
  window.trackCTAClick = trackCTAClick;
}

// 導出所有函數
export default {
  isGALoaded,
  trackEvent,
  trackPageView,
  trackSearch,
  trackViewTeacher,
  trackBeginBooking,
  trackCompleteBooking,
  trackFeatureUsage,
  trackCTAClick,
  getABVariant
};
